import React, { useMemo, useCallback } from 'react';
import { Box, Flex } from '@mantine/core';
import { FilterCategory } from '../types';
import LeftPanel, { type ChildFilterItem } from './left-panel/LeftPanel';
import RightPanel from './right-panel/RightPanel';
import { type SelectedFilterContentProps } from './right-panel/SelectedFilterContent';

export type { ChildFilterItem } from './left-panel/LeftPanel';

export interface FilterModalContainerProps extends SelectedFilterContentProps {
  ideaCategories: FilterCategory[];
  evidenceCategories: FilterCategory[];
  getCategoryTooltip: (categoryId: string, disabled: boolean) => string;
  getCategoryIcon: (categoryId: string) => React.ReactNode;
  handleCategorySelect: (categoryId: string, categoryType: 'idea' | 'evidence') => void;
  childFilters: ChildFilterItem[];
  categoryIdsWithChildren: Set<string>;
  searchInput: React.ReactNode;
  handleChildFilterSelect: (childFilterId: string) => void;
  middlePanelSearch: string;
  handleRemoveFilter: (filterName: string) => void;
  hasChildActiveFilters: () => boolean;
  additionalConfigurations: Array<{ id: string; label: string }>;
  additionalConfigurationsValues: Record<string, any>;
  setAdditionalConfigurationsValues: (
    value: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>)
  ) => void;
  isFromIdeas?: boolean;
  displayedCategory?: string;
  displayedChildFilter?: string | null;
  displayedCategoryType?: 'idea' | 'evidence' | null;
}

const filterHiddenFromSection = (section: Record<string, any> | undefined, isFromIdeas: boolean): Record<string, any> => {
  if (!section) return {};
  const isHiddenCheck = (config: any) =>
    isFromIdeas ? !!config.hidden : config.showInInbox === false;

  const filtered: Record<string, any> = {};
  Object.entries(section).forEach(([key, config]) => {
    if (key === '_id') return;
    // Hide per filter key, not by label — e.g. `owner` (hidden) must not remove `evidence-owner` (same label "Owner").
    if (isHiddenCheck(config)) return;
    if (config.childFilters) {
      const filteredChildren: Record<string, any> = {};
      Object.entries(config.childFilters).forEach(([childKey, childConfig]) => {
        if (!isHiddenCheck(childConfig)) {
          filteredChildren[childKey] = childConfig;
        }
      });
      filtered[key] = { ...config, childFilters: filteredChildren };
    } else {
      filtered[key] = config;
    }
  });
  return filtered;
};

const FilterModalContainer: React.FC<FilterModalContainerProps> = ({
  ideaCategories,
  evidenceCategories,
  getCategoryTooltip,
  getCategoryIcon,
  handleCategorySelect,
  selectedCategory,
  childFilters,
  categoryIdsWithChildren,
  searchInput,
  handleChildFilterSelect,
  middlePanelSearch,
  selectedChildFilter,
  handleRemoveFilter,
  hasChildActiveFilters,
  additionalConfigurations,
  additionalConfigurationsValues,
  setAdditionalConfigurationsValues,
  filtersConfig,
  localActiveFilters,
  setLocalActiveFilters,
  getFilterKey,
  setFetchableCheckboxCounts,
  clearTrigger,
  isFromIdeas = false,
  displayedCategory,
  displayedChildFilter,
  displayedCategoryType,
}) => {
  const isHidden = useCallback((config: any) =>
    isFromIdeas ? !!config.hidden : config.showInInbox === false,
  [isFromIdeas]);

  const visibleFiltersConfig = useMemo(() => ({
    idea: filterHiddenFromSection(filtersConfig?.idea, isFromIdeas),
    evidence: filterHiddenFromSection(filtersConfig?.evidence, isFromIdeas),
  }), [filtersConfig, isFromIdeas]);

  const hiddenCategoryIds = useMemo(() => {
    const ids = new Set<string>();
    const checkSection = (section: Record<string, any> | undefined) => {
      if (!section) return;
      Object.entries(section).forEach(([key, config]) => {
        if (key === '_id') return;
        if (isHidden(config)) ids.add(key);
      });
    };
    checkSection(filtersConfig?.idea);
    checkSection(filtersConfig?.evidence);

    // If a filter is explicitly visible in the evidence config, don't hide it
    // (prevents idea config's hidden flag from hiding the evidence version)
    if (filtersConfig?.evidence) {
      Object.entries(filtersConfig.evidence).forEach(([key, config]: [string, any]) => {
        if (key === '_id') return;
        if (ids.has(key) && config.showInInbox !== false && config.enabled !== false) {
          ids.delete(key);
        }
      });
    }

    return ids;
  }, [filtersConfig, isHidden]);

  const hiddenChildIds = useMemo(() => {
    const ids = new Set<string>();
    const checkSection = (section: Record<string, any> | undefined) => {
      if (!section) return;
      Object.entries(section).forEach(([, config]) => {
        if (config.childFilters) {
          Object.entries(config.childFilters).forEach(([childKey, childConfig]) => {
            if (isHidden(childConfig)) ids.add(childKey);
          });
        }
      });
    };
    checkSection(filtersConfig?.idea);
    checkSection(filtersConfig?.evidence);
    return ids;
  }, [filtersConfig, isHidden]);

  // A nested parent has no operation when every one of its children is hidden,
  // so we skip rendering it in the regular filter modal loop. Section is required
  // because the same category id (e.g. owner, product-area) can exist in both
  // idea and evidence configs with different childFilters.
  const hasAllChildrenHidden = useCallback(
    (categoryId: string, section: 'idea' | 'evidence'): boolean => {
      const primary = section === 'idea' ? filtersConfig?.idea : filtersConfig?.evidence;
      const fallback = section === 'idea' ? filtersConfig?.evidence : filtersConfig?.idea;
      const config = primary?.[categoryId] ?? fallback?.[categoryId];
      const children = (config as any)?.childFilters;
      if (!children || typeof children !== 'object') return false;
      const childKeys = Object.keys(children);
      if (childKeys.length === 0) return false;
      return childKeys.every((k) => hiddenChildIds.has(k));
    },
    [filtersConfig, hiddenChildIds]
  );

  const visibleIdeaCategories = useMemo(
    () => ideaCategories.filter(c => !hiddenCategoryIds.has(c.id) && !hasAllChildrenHidden(c.id, 'idea')),
    [ideaCategories, hiddenCategoryIds, hasAllChildrenHidden]
  );

  const visibleEvidenceCategories = useMemo(
    () => evidenceCategories.filter(c => !hiddenCategoryIds.has(c.id) && !hasAllChildrenHidden(c.id, 'evidence')),
    [evidenceCategories, hiddenCategoryIds, hasAllChildrenHidden]
  );

  const visibleChildFilters = useMemo(() =>
    childFilters.filter(c => !hiddenChildIds.has(c.id)), [childFilters, hiddenChildIds]);

  return (
    <Box className="filter-modal-container">
      <Flex justify="space-between" w="100%" wrap="nowrap" gap={0}>
        <Box className="filter-modal-container__left" py={12}>
          <LeftPanel
            ideaCategories={visibleIdeaCategories}
            evidenceCategories={visibleEvidenceCategories}
            getCategoryTooltip={getCategoryTooltip}
            getCategoryIcon={getCategoryIcon}
            handleCategorySelect={handleCategorySelect}
            selectedCategory={selectedCategory}
            childFilters={visibleChildFilters}
            categoryIdsWithChildren={categoryIdsWithChildren}
            searchInput={searchInput}
            handleChildFilterSelect={handleChildFilterSelect}
            middlePanelSearch={middlePanelSearch}
            isFromIdeas={isFromIdeas}
            localActiveFilters={localActiveFilters}
          />
        </Box>
        <Box className="filter-modal-container__right" pl={16} py={12}>
          <RightPanel
            selectedChildFilter={displayedChildFilter ?? selectedChildFilter}
            selectedCategory={displayedCategory ?? selectedCategory}
            selectedCategoryType={displayedCategoryType}
            handleRemoveFilter={handleRemoveFilter}
            hasChildActiveFilters={hasChildActiveFilters}
            filtersConfig={visibleFiltersConfig}
            localActiveFilters={localActiveFilters}
            setLocalActiveFilters={setLocalActiveFilters}
            getFilterKey={getFilterKey}
            setFetchableCheckboxCounts={setFetchableCheckboxCounts}
            clearTrigger={clearTrigger}
          />
        </Box>
        
      </Flex>
    </Box>
  );
};

export default FilterModalContainer;
